import React, { useEffect } from 'react'
import { Link } from 'react-router-dom'
import * as S from './styled'
import type { TabProps } from './types'

const isElementVisible = (el: HTMLElement | null, offset: number) => {
  if (!el) return false
  const rect = el.getBoundingClientRect()

  return (
    rect.top >= offset &&
    rect.left >= offset &&
    rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) - offset &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth) - offset
  )
}

const getComponent = ({ to }: Pick<TabProps, 'to'>) => {
  if (to) return Link

  return 'button'
}

export const Tab = ({
  name,
  direction = 'horizontal',
  size = 'medium',
  style,
  selected,
  variant = 'default',
  weight = 'normal',
  noWrap,
  bold,
  scrollable,
  scrollIntoView = false,
  scrollIntoViewOffset = 0,
  to,
  onClick,
  ...other
}: TabProps) => {
  const ref = React.useRef<HTMLButtonElement | null>(null)

  useEffect(() => {
    if (scrollIntoView && selected && !isElementVisible(ref.current, scrollIntoViewOffset)) {
      // setTimeout is needed to trigger scroll into view function, it does not work without it
      setTimeout(() => {
        ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' })
      }, 0)
    }
  }, [scrollIntoView, scrollIntoViewOffset, selected])

  return (
    <S.TabContainer
      $direction={direction}
      $marginSize={size}
      aria-current={selected ? 'page' : undefined}
      {...other}
    >
      <S.Tab
        ref={ref}
        type="button"
        as={getComponent({ to })}
        to={to}
        $isActive={selected}
        $direction={direction}
        $size={size}
        $variant={variant}
        $scrollable={scrollable}
        $weight={weight}
        style={style}
        onClick={onClick}
      >
        {name}
      </S.Tab>
    </S.TabContainer>
  )
}

Tab.tabsRole = 'Tab'
